/**
 * Asistente: un chat que contesta preguntas sobre los datos de la aplicación.
 *
 * «¿Cuándo vence el contrato de Cristina?», «pásame el IBI de PU5», «¿quién no ha pagado este
 * mes?». Detrás hay un modelo de OpenAI que consulta la base de datos con herramientas de SOLO
 * LECTURA (ver `apps/api/src/services/asistente`). No puede cambiar nada.
 *
 * LA CONVERSACIÓN VIVE AQUÍ: se manda entera en cada pregunta (el chat del servidor no guarda
 * estado) y la que está abierta se guarda en `sessionStorage` para que sobreviva a un cambio de
 * pantalla. Además, tras cada respuesta se guarda en el HISTORIAL del servidor, de quien
 * pregunta: desde «Conversaciones» se vuelve a cualquiera de las anteriores y se sigue donde se
 * dejó.
 *
 * Los documentos que adjunta salen como fichas con su ojo (visor) y su botón de descarga.
 */
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssistantAttachment, AssistantStoredMessage } from '@naar/shared';
import { ApiError, api } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { EmptyState } from '../components/EmptyState';
import { toast } from '../components/feedback/Toaster';
import { useDocumentViewer } from '../components/overlay/DocumentViewer';
import styles from './AssistantPage.module.css';

interface Mensaje extends AssistantStoredMessage {
  /** La respuesta no llegó: se enseña el error con «Reintentar». No se guarda. */
  error?: boolean;
}

interface Conversacion {
  /** `null` hasta la primera pregunta: lo pone la pantalla y con él se guarda en el historial. */
  id: string | null;
  mensajes: Mensaje[];
}

const CLAVE = 'naar:asistente:conversacion';
const HISTORIAL = ['assistant', 'conversations'] as const;

const SUGERENCIAS = [
  '¿Qué contratos vencen en los próximos 3 meses?',
  '¿Quién no ha pagado este mes?',
  '¿Qué tengo pendiente esta semana?',
  'Pásame el IBI de un piso',
  '¿Cuántas habitaciones libres tengo?',
  '¿Qué gastos ha tenido cada piso este mes?',
  '¿Cómo renuevo un contrato?',
];

const sinErrores = (mensajes: Mensaje[]) => mensajes.filter((mensaje) => !mensaje.error);

function leerConversacion(): Conversacion {
  try {
    const guardada = sessionStorage.getItem(CLAVE);
    const datos: unknown = guardada ? JSON.parse(guardada) : null;
    // Antes de haber historial se guardaba solo la lista de mensajes, sin id.
    if (Array.isArray(datos)) return { id: null, mensajes: sinErrores(datos as Mensaje[]) };
    if (datos && typeof datos === 'object' && Array.isArray((datos as Conversacion).mensajes)) {
      const { id, mensajes } = datos as Conversacion;
      return { id: typeof id === 'string' ? id : null, mensajes: sinErrores(mensajes) };
    }
  } catch {
    // Ilegible: se empieza de cero.
  }
  return { id: null, mensajes: [] };
}

function guardarEnPestana(conversacion: Conversacion) {
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify({ ...conversacion, mensajes: sinErrores(conversacion.mensajes) }));
  } catch {
    // Sin almacenamiento (modo privado, bloqueado): la conversación vive solo en memoria.
  }
}

export function AssistantPage() {
  const [inicial] = useState(leerConversacion);
  const [conversacionId, setConversacionId] = useState<string | null>(inicial.id);
  const [mensajes, setMensajes] = useState<Mensaje[]>(inicial.mensajes);
  const [borrador, setBorrador] = useState('');
  /** Solo en el móvil: el panel de conversaciones se abre encima del chat. */
  const [panelAbierto, setPanelAbierto] = useState(false);
  const listaRef = useRef<HTMLDivElement>(null);
  const entradaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  const estado = useQuery({ queryKey: ['assistant', 'status'], queryFn: () => api.assistantStatus(), staleTime: 60_000 });

  const guardar = useMutation({
    mutationFn: ({ id, mensajes: lista }: { id: string; mensajes: Mensaje[] }) =>
      api.saveAssistantConversation(
        id,
        sinErrores(lista).map(({ error: _error, ...mensaje }) => mensaje),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: HISTORIAL }),
    onError: () => toast.error('La respuesta no se ha podido guardar en el historial.'),
  });

  const preguntar = useMutation({
    mutationFn: ({ historia }: { id: string; historia: Mensaje[] }) =>
      api.assistantChat(historia.map(({ role, content }) => ({ role, content }))),
    onSuccess: (respuesta, { id, historia }) => {
      const completa: Mensaje[] = [
        ...historia,
        {
          role: 'assistant',
          content: respuesta.reply,
          attachments: respuesta.attachments,
          consulted: respuesta.consulted,
        },
      ];
      setMensajes(completa);
      guardar.mutate({ id, mensajes: completa });
    },
    onError: (error: unknown) => {
      setMensajes((actuales) => [
        ...actuales,
        {
          role: 'assistant',
          content: error instanceof ApiError ? error.message : 'No he podido contestar. Vuelve a intentarlo.',
          error: true,
        },
      ]);
    },
  });

  useEffect(() => guardarEnPestana({ id: conversacionId, mensajes }), [conversacionId, mensajes]);

  // Siempre a la vista lo último: la respuesta nueva o el «pensando».
  useEffect(() => {
    const lista = listaRef.current;
    if (lista) lista.scrollTo({ top: lista.scrollHeight, behavior: 'smooth' });
  }, [mensajes.length, preguntar.isPending]);

  function enviar(texto: string) {
    const pregunta = texto.trim();
    if (!pregunta || preguntar.isPending) return;
    // Un error anterior no se manda como parte de la conversación.
    const historia: Mensaje[] = [...sinErrores(mensajes), { role: 'user', content: pregunta }];
    const id = conversacionId ?? crypto.randomUUID();
    setConversacionId(id);
    setMensajes(historia);
    setBorrador('');
    preguntar.mutate({ id, historia });
    entradaRef.current?.focus();
  }

  function reintentar() {
    const historia = sinErrores(mensajes);
    if (historia.at(-1)?.role !== 'user') return;
    const id = conversacionId ?? crypto.randomUUID();
    setConversacionId(id);
    setMensajes(historia);
    preguntar.mutate({ id, historia });
  }

  function nuevaConversacion() {
    setConversacionId(null);
    setMensajes([]);
    setBorrador('');
    entradaRef.current?.focus();
  }

  async function abrirConversacion(id: string) {
    if (id === conversacionId) return;
    try {
      const conversacion = await queryClient.fetchQuery({
        queryKey: [...HISTORIAL, id],
        queryFn: () => api.assistantConversation(id),
        staleTime: 0,
      });
      setConversacionId(conversacion.id);
      setMensajes(conversacion.messages);
      setBorrador('');
      entradaRef.current?.focus();
    } catch {
      toast.error('No se ha podido abrir la conversación.');
    }
  }

  function conversacionBorrada(id: string) {
    if (id === conversacionId) nuevaConversacion();
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    enviar(borrador);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter envía; Mayús+Enter salta de línea, como en cualquier chat.
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      enviar(borrador);
    }
  }

  const noDisponible = estado.data && !estado.data.available;

  return (
    <section className={styles.pagina}>
      <PageHeader
        title="Asistente IA"
        subtitle="Pregunta lo que quieras sobre tus pisos, inquilinos, contratos, cobros y documentos."
        actions={
          noDisponible ? undefined : (
            // En escritorio el historial está siempre a la izquierda; en el móvil se abre con esto.
            <div className={styles.soloMovil}>
              <Button variant="secondary" onClick={() => setPanelAbierto(true)}>
                <Icon name="chat" size={16} />
                Conversaciones
              </Button>
            </div>
          )
        }
      />

      {noDisponible ? (
        <Card>
          <EmptyState
            icon="chat"
            title="El Asistente no está activado"
            body="Necesita una clave de OpenAI en el servidor (OPENAI_API_KEY en el .env). Con la clave puesta y la API reiniciada, esta pantalla se activa sola."
          />
        </Card>
      ) : (
        <Card className={styles.chat}>
          {/* El historial, a la izquierda: nueva conversación arriba y las anteriores debajo. */}
          <aside
            className={panelAbierto ? `${styles.panel} ${styles.panelAbierto}` : styles.panel}
            aria-label="Conversaciones"
          >
            <PanelHistorial
              actualId={conversacionId}
              ocupado={preguntar.isPending}
              onNueva={() => {
                setPanelAbierto(false);
                nuevaConversacion();
              }}
              onAbrir={(id) => {
                setPanelAbierto(false);
                void abrirConversacion(id);
              }}
              onBorrada={conversacionBorrada}
              onCerrar={() => setPanelAbierto(false)}
            />
          </aside>
          {panelAbierto && (
            <button
              type="button"
              className={styles.panelVelo}
              onClick={() => setPanelAbierto(false)}
              aria-label="Cerrar las conversaciones"
            />
          )}

          <div className={styles.conversacion}>
          <div className={styles.lista} ref={listaRef} aria-live="polite">
            {mensajes.length === 0 ? (
              <Bienvenida onElegir={enviar} />
            ) : (
              mensajes.map((mensaje, index) => (
                <Burbuja
                  key={index}
                  mensaje={mensaje}
                  onReintentar={mensaje.error && index === mensajes.length - 1 ? reintentar : undefined}
                />
              ))
            )}
            {preguntar.isPending && <Pensando />}
          </div>

          <form className={styles.composer} onSubmit={onSubmit}>
            <textarea
              ref={entradaRef}
              className={styles.entrada}
              value={borrador}
              onChange={(event) => setBorrador(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Escribe tu pregunta…"
              aria-label="Pregunta para el Asistente"
              rows={1}
              maxLength={4000}
              autoFocus
            />
            <button
              type="submit"
              className={styles.enviar}
              disabled={!borrador.trim() || preguntar.isPending}
              aria-label="Enviar"
              title="Enviar (Enter)"
            >
              <Icon name="send" size={18} strokeWidth={2} />
            </button>
          </form>
          <p className={styles.nota}>
            Solo consulta: no cambia nada. Puede equivocarse; lo importante, compruébalo en su ficha.
          </p>
          </div>
        </Card>
      )}
    </section>
  );
}

/** «Hoy, 14:32», «Ayer», «12 oct» o «12 oct 2025». */
function cuando(iso: string): string {
  const fecha = new Date(iso);
  const hoy = new Date();
  const dia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dias = Math.round((dia(hoy) - dia(fecha)) / 86_400_000);
  if (dias === 0) return `Hoy, ${fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  if (dias === 1) return 'Ayer';
  return fecha.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    ...(fecha.getFullYear() === hoy.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * El historial de conversaciones, en el panel de la izquierda: «Nueva conversación» arriba y,
 * debajo, las anteriores de la más reciente a la más antigua. La abierta va marcada.
 */
function PanelHistorial({
  actualId,
  ocupado,
  onNueva,
  onAbrir,
  onBorrada,
  onCerrar,
}: {
  actualId: string | null;
  ocupado: boolean;
  onNueva: () => void;
  onAbrir: (id: string) => void;
  onBorrada: (id: string) => void;
  /** Solo en el móvil, donde el panel tapa el chat. */
  onCerrar: () => void;
}) {
  const queryClient = useQueryClient();

  const lista = useQuery({
    queryKey: HISTORIAL,
    queryFn: () => api.assistantConversations(),
    staleTime: 30_000,
  });

  const borrar = useMutation({
    mutationFn: (id: string) => api.deleteAssistantConversation(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: HISTORIAL });
      onBorrada(id);
      toast.info('Conversación borrada.');
    },
    onError: () => toast.error('No se ha podido borrar la conversación.'),
  });

  const conversaciones = lista.data?.data ?? [];

  return (
    <>
      <div className={styles.panelCabecera}>
        <button type="button" className={styles.panelNueva} onClick={onNueva} disabled={ocupado}>
          <Icon name="plus" size={15} strokeWidth={2.2} />
          Nueva conversación
        </button>
        <button type="button" className={styles.panelCerrar} onClick={onCerrar} aria-label="Cerrar las conversaciones">
          <Icon name="close" size={16} strokeWidth={2} />
        </button>
      </div>
      <p className={styles.panelTitulo}>Conversaciones</p>
      <div className={styles.panelLista}>
        {lista.isPending ? (
          <p className={styles.historialVacio}>Cargando…</p>
        ) : lista.isError ? (
          <p className={styles.historialVacio}>No se ha podido cargar el historial.</p>
        ) : conversaciones.length === 0 ? (
          <p className={styles.historialVacio}>Todavía no hay conversaciones guardadas.</p>
        ) : (
          conversaciones.map((conversacion) => {
            const actual = conversacion.id === actualId;
            return (
              <div
                key={conversacion.id}
                className={actual ? `${styles.historialFila} ${styles.historialActual}` : styles.historialFila}
              >
                <button
                  type="button"
                  className={styles.historialAbrir}
                  aria-current={actual ? 'true' : undefined}
                  disabled={ocupado && !actual}
                  onClick={() => onAbrir(conversacion.id)}
                >
                  <span className={styles.historialTitulo} title={conversacion.title}>
                    {conversacion.title}
                  </span>
                  <span className={styles.historialFecha}>
                    {actual ? 'Abierta ahora' : cuando(conversacion.updatedAt)}
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.historialBorrar}
                  onClick={() => borrar.mutate(conversacion.id)}
                  disabled={borrar.isPending}
                  title="Borrar conversación"
                  aria-label={`Borrar «${conversacion.title}»`}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

function Bienvenida({ onElegir }: { onElegir: (texto: string) => void }) {
  return (
    <div className={styles.bienvenida}>
      <span className={styles.bienvenidaIcono} aria-hidden="true">
        <Icon name="chat" size={26} />
      </span>
      <h2 className={styles.bienvenidaTitulo}>¿En qué te ayudo?</h2>
      <p className={styles.bienvenidaTexto}>
        Pregúntame por un inquilino, un piso, un contrato o un documento, o cómo se hace algo en la
        aplicación. Por ejemplo, «¿cuándo renueva el contrato de Cristina?», «pásame la escritura de
        PU5» o «¿por qué no me deja borrar esta habitación?».
      </p>
      <div className={styles.sugerencias}>
        {SUGERENCIAS.map((sugerencia) => (
          <button key={sugerencia} type="button" className={styles.sugerencia} onClick={() => onElegir(sugerencia)}>
            {sugerencia}
          </button>
        ))}
      </div>
    </div>
  );
}

function Pensando() {
  return (
    <div className={`${styles.fila} ${styles.filaAsistente}`}>
      <div className={`${styles.burbuja} ${styles.burbujaAsistente} ${styles.pensando}`} role="status">
        <span className={styles.puntos} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        Consultando…
      </div>
    </div>
  );
}

function Burbuja({ mensaje, onReintentar }: { mensaje: Mensaje; onReintentar?: (() => void) | undefined }) {
  const esUsuario = mensaje.role === 'user';
  return (
    <div className={`${styles.fila} ${esUsuario ? styles.filaUsuario : styles.filaAsistente}`}>
      <div
        className={[
          styles.burbuja,
          esUsuario ? styles.burbujaUsuario : styles.burbujaAsistente,
          mensaje.error ? styles.burbujaError : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {esUsuario ? <p className={styles.parrafo}>{mensaje.content}</p> : <Texto texto={mensaje.content} />}

        {mensaje.attachments && mensaje.attachments.length > 0 && (
          <div className={styles.adjuntos}>
            {mensaje.attachments.map((adjunto) => (
              <Adjunto key={adjunto.documentId} adjunto={adjunto} />
            ))}
          </div>
        )}

        {onReintentar && (
          <button type="button" className={styles.reintentar} onClick={onReintentar}>
            Reintentar
          </button>
        )}
      </div>
      {mensaje.consulted && mensaje.consulted.length > 0 && (
        <span className={styles.consultado}>Consultó: {mensaje.consulted.join(', ')}</span>
      )}
    </div>
  );
}

function Adjunto({ adjunto }: { adjunto: AssistantAttachment }) {
  const visor = useDocumentViewer();
  function descargar() {
    void api
      .downloadDocument(adjunto.documentId, adjunto.name)
      .catch(() => toast.error('No se ha podido descargar el documento.'));
  }
  return (
    <div className={styles.adjunto}>
      <button type="button" className={styles.adjuntoHoja} onClick={descargar} title="Descargar" aria-label={`Descargar ${adjunto.name}`}>
        <Icon name="file-lines" size={18} color="var(--naar-brand-ink)" />
      </button>
      <span className={styles.adjuntoTexto}>
        <span className={styles.adjuntoNombre} title={adjunto.name}>
          {adjunto.name}
        </span>
        {adjunto.meta && <span className={styles.adjuntoMeta}>{adjunto.meta}</span>}
      </span>
      <button
        type="button"
        className={styles.adjuntoAccion}
        onClick={() => visor.open({ id: adjunto.documentId, name: adjunto.name, meta: adjunto.meta })}
        title="Ver el documento"
        aria-label={`Ver ${adjunto.name}`}
      >
        <Icon name="eye" size={16} strokeWidth={2} />
      </button>
      <button type="button" className={styles.adjuntoAccion} onClick={descargar} title="Descargar" aria-label={`Descargar ${adjunto.name}`}>
        <Icon name="download" size={16} strokeWidth={2} />
      </button>
    </div>
  );
}

/**
 * El texto del Asistente, con lo poco de formato que se le permite: párrafos, listas con «-» o
 * «1.» y **negrita**. Se pinta con React, nunca como HTML: lo que diga el modelo no puede
 * colar etiquetas en la página.
 */
function Texto({ texto }: { texto: string }) {
  const bloques: ReactNode[] = [];
  const lineas = texto.replace(/\r\n/g, '\n').split('\n');
  let lista: { ordenada: boolean; items: string[] } | null = null;
  let parrafo: string[] = [];

  const cerrarParrafo = () => {
    if (parrafo.length > 0) {
      bloques.push(
        <p key={`p${bloques.length}`} className={styles.parrafo}>
          {enLinea(parrafo.join(' '))}
        </p>,
      );
      parrafo = [];
    }
  };
  const cerrarLista = () => {
    if (lista) {
      const items = lista.items.map((item, index) => <li key={index}>{enLinea(item)}</li>);
      bloques.push(
        lista.ordenada ? (
          <ol key={`l${bloques.length}`} className={styles.lista_}>{items}</ol>
        ) : (
          <ul key={`l${bloques.length}`} className={styles.lista_}>{items}</ul>
        ),
      );
      lista = null;
    }
  };

  for (const linea of lineas) {
    const vineta = /^\s*[-*•]\s+(.*)$/.exec(linea);
    const numero = /^\s*\d+[.)]\s+(.*)$/.exec(linea);
    if (vineta || numero) {
      cerrarParrafo();
      const ordenada = Boolean(numero);
      if (!lista || lista.ordenada !== ordenada) {
        cerrarLista();
        lista = { ordenada, items: [] };
      }
      lista.items.push((vineta ?? numero)![1]!);
    } else if (!linea.trim()) {
      cerrarParrafo();
      cerrarLista();
    } else {
      cerrarLista();
      parrafo.push(linea.trim());
    }
  }
  cerrarParrafo();
  cerrarLista();
  return <>{bloques}</>;
}

/** `**negrita**` dentro de una línea. */
function enLinea(texto: string): ReactNode[] {
  return texto.split(/(\*\*[^*]+\*\*)/g).map((trozo, index) =>
    trozo.startsWith('**') && trozo.endsWith('**') && trozo.length > 4 ? (
      <strong key={index}>{trozo.slice(2, -2)}</strong>
    ) : (
      trozo
    ),
  );
}
